import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { OutlinedInput, FormLabel, FormControl, FormHelperText, Typography } from '@mui/material';

import RequiredLabel from '@ui-elements/RequiredLabel';
import InputValidationErrorBox from '@ui-parts/InputValidationErrorBox';
import HelpLink from '@ui-parts/HelpLink';

import { propTypesNullable } from '@functional';

import { PALETTE, BASE_FONT_COLOR, FONT_SIZES } from '~/assets/styles/base';

const StyledFormControl = styled(FormControl)`
  width: ${({ $width }) => $width};
`;

const StyledFormLabel = styled(FormLabel)`
  padding: ${({ $label, $description }) => {
    if ($label && $description) {
      return '8px 0 0 0';
    }
    if ($label) {
      return '8px 0';
    }
    return '0';
  }};
  font-weight: ${({ $fontWeight }) => $fontWeight};
`;

const StyledDescriptionWrapper = styled.span`
  padding: ${({ padding }) => padding};
  color: ${BASE_FONT_COLOR};
  background: ${({ background }) => background};
  white-space: pre-line;
  margin-top: 10px;
  width: 100%;
`;

const StyleOutlinedInput = styled(OutlinedInput)`
  &.MuiOutlinedInput-root {
    padding: 8px 15px;
    background: ${PALETTE.white};
  }
  &.MuiOutlinedInput-root .MuiOutlinedInput-input {
    color: ${PALETTE.text.main};
    font-size: ${({ $inputFontSize }) => $inputFontSize};
    padding: 0;
    background: ${PALETTE.white};
    text-align: ${({ $textAlign }) => $textAlign};
    width: ${({ $width }) => $width};
    -moz-appearance: textfield;
  }

  /* 自動補完時のbg対応 */
  &.MuiOutlinedInput-root .MuiOutlinedInput-input:-webkit-autofill,
  &.MuiOutlinedInput-root .MuiOutlinedInput-input:-webkit-autofill:hover,
  &.MuiOutlinedInput-root .MuiOutlinedInput-input:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0px 1000px #ffffff inset !important;
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    pointer-events: none;
    margin: 0;
    width: 0;
  }
  input::placeholder {
    font-size: ${FONT_SIZES.bodyDefault};
  }
`;

const ValidationError = styled(FormHelperText)`
  margin-top: 10px;
  min-width: 200px;
`;

const FlexWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const StyledDesc = styled.span`
  font-size: ${({ fontSize }) => fontSize};
  line-height: 1.5;
  & > a {
    color: ${PALETTE.text.primary};
    text-decoration: none;
  }
  & > a:visited {
    color: ${PALETTE.text.primary};
    text-decoration: none;
  }
`;

const InputWithLabel = forwardRef(
  (
    {
      label = null,
      name,
      type = 'text',
      value = '',
      required = false,
      disabled = false,
      placeholder = '',
      helperText = '',
      errorMessage = '',
      width = '300px',
      textAlign = 'left',
      handleChange,
      handleBlur = null,
      error = false,
      errorWidth = null,
      dataTestId,
      fontWeight = 'inherit',
      dangerousSetDescription = false,
      description = null,
      warnText = '',
      warnTop = '',
      warnRight = '',
      warnLeft = '',
      warnBottom = '',
      warnBorder = '',
      warnBorderRadius = '',
      warnBackground = '',
      warnLink = '',
      background = PALETTE.white,
      fontSize = dangerousSetDescription ? FONT_SIZES.body3 : 'body3',
      inputFontSize = FONT_SIZES.bodyDefault,
      padding = '0 0 15px 0',
      inputProps = {},
      handleOnKeyDown = null,
      autoComplete = 'on',
      position = 'center',
      multiline = false,
      rows = 1,
    },
    ref
  ) => {
    return (
      <StyledFormControl $width={width}>
        {required && label ? (
          <StyledFormLabel
            data-testid={`uiParts-InputWithLabel-lbl-req-${dataTestId}`}
            $label={label}
            $description={description}
            $fontWeight={fontWeight}
          >
            <RequiredLabel />
            <Typography variant="body3" color="text.main">
              {label}
            </Typography>
          </StyledFormLabel>
        ) : (
          <StyledFormLabel
            data-testid={`uiParts-InputWithLabel-lbl-opt-${dataTestId}`}
            $label={label}
            $description={description}
            $fontWeight={fontWeight}
          >
            <Typography variant="body3" color="text.main">
              {label}
            </Typography>
          </StyledFormLabel>
        )}
        {description && (
          <FlexWrap>
            <StyledDescriptionWrapper
              background={background}
              padding={padding}
              data-testid={`uiParts-InputWithLabel-lbl-desc-${dataTestId}`}
            >
              {dangerousSetDescription ? (
                <StyledDesc
                  fontSize={fontSize}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: description,
                  }}
                  data-testid="uiParts-InputWithLabel-lbl-dangerouslySetDescription"
                />
              ) : (
                <Typography variant={fontSize} data-testid="uiParts-InputWithLabel-lbl-description">
                  {description}
                </Typography>
              )}
            </StyledDescriptionWrapper>
            {warnText && (
              <HelpLink
                warnText={warnText}
                warnTop={warnTop}
                warnRight={warnRight}
                warnBottom={warnBottom}
                warnLeft={warnLeft}
                warnLink={warnLink}
                warnBorder={warnBorder}
                warnBorderRadius={warnBorderRadius}
                warnBackground={warnBackground}
              />
            )}
          </FlexWrap>
        )}
        {error && errorMessage ? (
          <>
            <StyleOutlinedInput
              type={type}
              name={name}
              value={value}
              disabled={disabled}
              onChange={handleChange}
              onBlur={handleBlur}
              ref={ref}
              error={error}
              placeholder={placeholder}
              inputProps={{
                'data-testid': `uiParts-InputWithLabel-input-${dataTestId}`,
                autoComplete,
                ...inputProps,
              }}
              $width={width}
              $textAlign={textAlign}
              $fontWeight={fontWeight}
              $inputFontSize={inputFontSize}
              onKeyDown={handleOnKeyDown}
              multiline={multiline}
              rows={rows}
            />
            <ValidationError
              component={InputValidationErrorBox}
              message={errorMessage}
              dataTestId={dataTestId}
              position={position}
              width={errorWidth}
            />
          </>
        ) : (
          <StyleOutlinedInput
            type={type}
            name={name}
            value={value}
            disabled={disabled}
            onChange={handleChange}
            onBlur={handleBlur}
            ref={ref}
            error={error}
            placeholder={placeholder}
            inputProps={{
              'data-testid': `uiParts-InputWithLabel-input-${dataTestId}`,
              autoComplete,
              ...inputProps,
            }}
            $inputFontSize={inputFontSize}
            $width={width}
            $textAlign={textAlign}
            $fontWeight={fontWeight}
            onKeyDown={handleOnKeyDown}
            multiline={multiline}
            rows={rows}
          />
        )}
        <FormHelperText>{helperText}</FormHelperText>
      </StyledFormControl>
    );
  }
);
InputWithLabel.displayName = 'InputWithLabel';

InputWithLabel.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  type: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  helperText: PropTypes.string,
  errorMessage: PropTypes.string,
  dangerousSetDescription: PropTypes.bool,
  description: PropTypes.string,
  warnText: PropTypes.string,
  warnTop: PropTypes.string,
  warnLeft: PropTypes.string,
  warnRight: PropTypes.string,
  warnBottom: PropTypes.string,
  warnLink: PropTypes.string,
  warnBorder: PropTypes.string,
  warnBorderRadius: PropTypes.string,
  warnBackground: PropTypes.string,
  width: PropTypes.string,
  textAlign: PropTypes.oneOf(['center', 'left', 'right']),
  autoComplete: PropTypes.oneOf(['on', 'off']),
  handleChange: propTypesNullable(PropTypes.func.isRequired), // react-form-hookのonChangeで制御する場合もあるのでnullを許容
  handleBlur: PropTypes.func,
  handleOnKeyDown: PropTypes.func,
  error: PropTypes.bool,
  errorWidth: PropTypes.string,
  dataTestId: PropTypes.string.isRequired,
  fontWeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  background: PropTypes.string,
  fontSize: PropTypes.string,
  inputFontSize: PropTypes.string,
  padding: PropTypes.string,
  position: PropTypes.string,
  // ↓inputPropsは動的なのでいったんlintエラーは無視してobjectで定義
  // eslint-disable-next-line react/forbid-prop-types
  inputProps: PropTypes.object,
  multiline: PropTypes.bool,
  rows: PropTypes.number,
};

export default InputWithLabel;
